import { isRecord } from '@ardoq/common-helpers';
import { AggregatedGraphEdge } from './AggregatedGraphEdge';
import { CollapsibleGraphGroup } from './CollapsibleGraphGroup';
import { ComponentGraphEdge } from './ComponentGraphEdge';
import { ComponentGraphGroup } from './ComponentGraphGroup';
import { ComponentGraphNode } from './ComponentGraphNode';
import { ComponentGraphParentChildEdge } from './ComponentGraphParentChildEdge';
import { GraphEdge } from './GraphEdge';
import { GraphItem } from './GraphItem';
import { GraphNode } from './GraphNode';
import { MetamodelViewGraphEdge } from './MetamodelViewGraphEdge';
import { MetaModelViewGraphNode } from './MetaModelViewGraphNode';
import { ParentChildGraphEdge } from './ParentChildGraphEdge';
import { ComponentGraphAggregatedEdgeModel } from './types';
import { GraphItemsModel } from '../viewModel/types';

export const isComponentGraphNode = (node: unknown) =>
  node instanceof GraphNode || node instanceof ComponentGraphNode;

export const isNode = (node: unknown) =>
  isComponentGraphNode(node) || node instanceof MetaModelViewGraphNode;

export const isComponentGraphEdgeModel = (edge: unknown) =>
  edge instanceof GraphEdge || edge instanceof ComponentGraphEdge;

export const isCollapsibleGroup = (group: unknown) =>
  group instanceof CollapsibleGraphGroup ||
  group instanceof ComponentGraphGroup;

export const isComponentGraphEdge = (
  edge: object
): edge is ComponentGraphEdge =>
  edge && 'getUrlFieldValues' in edge && 'getCssColorAttributes' in edge;

export const isAggregatedGraphEdge = (
  edge: object
): edge is ComponentGraphAggregatedEdgeModel =>
  Boolean(
    edge &&
    'isAggregated' in edge &&
    edge.isAggregated &&
    (edge as AggregatedGraphEdge | ComponentGraphEdge).modelIds?.length
  );

export const isParentChildGraphEdge = (edge: unknown) =>
  edge instanceof ParentChildGraphEdge ||
  edge instanceof ComponentGraphParentChildEdge;

export const isAnyComponentGraphEdge = (edge: object) =>
  isComponentGraphEdgeModel(edge) ||
  isAggregatedGraphEdge(edge) ||
  isParentChildGraphEdge(edge);

export const isEdge = (edge: object) =>
  isAnyComponentGraphEdge(edge) || edge instanceof MetamodelViewGraphEdge;

export const hasGraphItemMethods = (
  maybeGraphItem: object
): maybeGraphItem is GraphItem =>
  maybeGraphItem &&
  'isComponent' in maybeGraphItem &&
  'isReference' in maybeGraphItem;

export const hasColorAttributes = (
  maybeGraphItem: object
): maybeGraphItem is
  | ComponentGraphEdge
  | ComponentGraphGroup
  | ComponentGraphNode =>
  maybeGraphItem && 'getCssColorAttributes' in maybeGraphItem;

export const hasContextAttributes = (
  maybeGraphItem: object
): maybeGraphItem is ComponentGraphGroup | ComponentGraphNode =>
  maybeGraphItem && 'getIsContext' in maybeGraphItem;

/**
 * Structural check for the `{ byId, add, update }` shape published by any
 * view backed by a `GraphItemsModel` — not specific to relationships. Shared
 * with other views (e.g. modernized block diagram) that publish the same
 * group model but nothing else relationships-specific.
 */
export const hasGraphItemsGroups = (
  viewProperties: Record<string, unknown>
): viewProperties is {
  groups: Pick<
    GraphItemsModel<CollapsibleGraphGroup | ComponentGraphGroup>,
    'byId' | 'add' | 'update'
  >;
} => {
  const { groups } = viewProperties;

  return (
    isRecord(groups) &&
    groups.byId instanceof Map &&
    Array.isArray(groups.add) &&
    Array.isArray(groups.update)
  );
};
